<!-- frontend/src/views/EssaySubmission.vue -->
<template>
  <div class="essay-submission">
    <h2>心得繳交</h2>

    <div class="content">
      <div class="info-section">
        <h3>心得繳交說明</h3>
        <ul>
          <li>參與「需繳交心得」的活動後，請於期限內上傳學習心得</li>
          <li>檔案格式限 <strong>PDF</strong>，大小上限 <strong>5MB</strong></li>
          <li>上傳後狀態為「審核中」，經管理者 / 助教審核通過後列入「已繳交心得」</li>
          <li>審核退回者可重新上傳，新檔會覆蓋舊檔</li>
        </ul>
      </div>

      <!-- 待繳交心得活動 -->
      <div class="submission-section">
        <h3>待繳交心得活動</h3>

        <div v-if="loading" class="loading">載入中...</div>
        <div v-else-if="pendingEssays.length === 0" class="no-data">目前沒有待繳交的心得</div>

        <div v-else class="table-wrap">
          <table class="essay-table">
            <thead>
              <tr>
                <th>活動名稱</th>
                <th>活動日期</th>
                <th>繳交期限</th>
                <th>通過可得點數</th>
                <th>狀態 / 檔案</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in pendingEssays" :key="item.activity_id">
                <td>{{ item.activity_title }}</td>
                <td>{{ formatDate(item.start_date) }}</td>
                <td :class="{ overdue: item.is_closed }">
                  <template v-if="item.deadline">
                    截止：{{ formatDateYMD(item.deadline) }}
                    <span v-if="item.is_closed" class="overdue-tag">已逾期</span>
                  </template>
                  <span v-else class="status-muted">無截止日</span>
                </td>
                <td>通過可得 <strong>{{ item.reflection_points || 0 }}</strong> 點</td>
                <td>
                  <template v-if="item.status === 'not_submitted'">
                    <span class="status-muted">尚未上傳</span>
                  </template>
                  <template v-else>
                    <div class="file-line">📄 {{ item.file_name }}</div>
                    <div class="date-line">上傳：{{ formatDateTime(item.submitted_at) }}</div>
                    <span :class="['status-badge', item.status]">{{ statusText(item.status) }}</span>
                    <div v-if="item.status === 'rejected' && item.review_comment" class="reject-note">
                      退回原因：{{ item.review_comment }}
                    </div>
                  </template>
                </td>
                <td>
                  <button
                    class="submit-btn"
                    @click="triggerUpload(item)"
                    :disabled="uploadingId === item.activity_id"
                  >
                    {{ uploadButtonText(item) }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 已繳交心得（已通過） -->
      <div class="history-section">
        <h3>已繳交心得</h3>

        <div v-if="submittedEssays.length === 0" class="no-data">尚無已通過的心得</div>

        <div v-else class="table-wrap">
          <table class="essay-table">
            <thead>
              <tr>
                <th>活動名稱</th>
                <th>繳交日期</th>
                <th>檔案</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in submittedEssays" :key="item.activity_id">
                <td>{{ item.activity_title }}</td>
                <td>{{ formatDateTime(item.submitted_at) }}</td>
                <td>📄 {{ item.file_name }}</td>
                <td><span class="status-badge approved">已通過</span></td>
                <td><button class="view-btn" @click="viewFile(item.reflection_id)">查看</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 心得審核（admin / TA） -->
      <div v-if="canReview" class="review-section">
        <h3>心得審核（管理者 / 助教）</h3>

        <div v-if="reviewList.length === 0" class="no-data">目前沒有心得可審核</div>

        <div v-else class="table-wrap">
          <table class="essay-table">
            <thead>
              <tr>
                <th>活動名稱</th>
                <th>繳交者</th>
                <th>檔案</th>
                <th>繳交時間</th>
                <th>狀態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in reviewList" :key="r.id">
                <td>{{ r.activity_title }}</td>
                <td>{{ r.name }}（{{ r.user_id }}）</td>
                <td>📄 {{ r.original_name }}</td>
                <td>{{ formatDateTime(r.submitted_at) }}</td>
                <td><span :class="['status-badge', r.status]">{{ statusText(r.status) }}</span></td>
                <td class="review-actions">
                  <button class="view-btn" @click="viewFile(r.id)">查看</button>
                  <button class="approve-btn" @click="doReview(r, 'approved')" :disabled="r.status === 'approved'">通過</button>
                  <button class="reject-btn" @click="doReview(r, 'rejected')">退回</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 隱藏的檔案輸入 -->
    <input
      ref="fileInput"
      type="file"
      accept="application/pdf,.pdf"
      style="display: none"
      @change="onFileChosen"
    />
  </div>
</template>

<script>
import {
  getMyReflections,
  uploadReflection,
  downloadReflectionFile,
  getReflectionsForReview,
  reviewReflection
} from '@/api.js'

const MAX_BYTES = 5 * 1024 * 1024

export default {
  name: 'EssaySubmission',
  data() {
    return {
      loading: false,
      pendingEssays: [],
      submittedEssays: [],
      reviewList: [],
      canReview: false,
      uploadingId: null,
      targetItem: null
    }
  },
  async created() {
    const info = JSON.parse(localStorage.getItem('user_info') || '{}')
    this.canReview =
      info.role === 'admin' ||
      info.role === 'TA' ||
      String(info.user_id || '').toUpperCase().startsWith('TA')
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await getMyReflections()
        this.pendingEssays = res.data.pending || []
        this.submittedEssays = res.data.submitted || []
        if (this.canReview) {
          await this.loadReviewList()
        }
      } catch (error) {
        console.error('載入心得資料失敗:', error)
      } finally {
        this.loading = false
      }
    },

    async loadReviewList() {
      try {
        const res = await getReflectionsForReview()
        this.reviewList = res.data
      } catch (error) {
        console.error('載入審核清單失敗:', error)
      }
    },

    uploadButtonText(item) {
      if (this.uploadingId === item.activity_id) return '上傳中...'
      return item.status === 'not_submitted' ? '繳交' : '重新上傳'
    },

    triggerUpload(item) {
      this.targetItem = item
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },

    async onFileChosen(event) {
      const file = event.target.files[0]
      if (!file || !this.targetItem) return
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        alert('僅接受 PDF 檔案')
        return
      }
      if (file.size > MAX_BYTES) {
        alert('檔案超過 5MB 上限')
        return
      }
      const item = this.targetItem
      this.uploadingId = item.activity_id
      try {
        await uploadReflection(item.activity_id, file)
        alert('上傳成功，狀態為「審核中」')
        await this.loadData()
      } catch (error) {
        console.error('心得上傳失敗:', error)
        alert(error.response?.data?.detail || '上傳失敗')
      } finally {
        this.uploadingId = null
        this.targetItem = null
      }
    },

    async viewFile(reflectionId) {
      try {
        const res = await downloadReflectionFile(reflectionId)
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (error) {
        console.error('開啟心得檔案失敗:', error)
        alert('無法開啟檔案')
      }
    },

    async doReview(r, status) {
      let comment = ''
      if (status === 'rejected') {
        comment = prompt('請輸入退回原因（可留白）', '') || ''
      }
      if (!confirm(status === 'approved' ? '確定通過此心得？' : '確定退回此心得？')) return
      try {
        await reviewReflection(r.id, status, comment)
        await this.loadData()
      } catch (error) {
        console.error('審核心得失敗:', error)
        alert(error.response?.data?.detail || '審核失敗')
      }
    },

    statusText(s) {
      const map = {
        not_submitted: '尚未上傳',
        pending: '審核中',
        approved: '已通過',
        rejected: '已退回'
      }
      return map[s] || s
    },

    formatDate(d) {
      return d ? new Date(d).toLocaleDateString('zh-TW') : '待定'
    },

    formatDateTime(d) {
      return d ? new Date(d).toLocaleString('zh-TW') : '—'
    },

    // 以 YYYY-MM-DD 顯示截止日（截止時間由後端計算：活動結束日 + N 天）
    formatDateYMD(d) {
      if (!d) return '無截止日'
      const dt = new Date(d)
      if (isNaN(dt.getTime())) return '無截止日'
      const p = n => String(n).padStart(2, '0')
      return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`
    }
  }
}
</script>

<style scoped>
.essay-submission {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
}

h2 {
  color: #333;
  margin-bottom: 30px;
  text-align: center;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.info-section {
  background: #e3f2fd;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.info-section h3 {
  color: #1976d2;
  margin-bottom: 15px;
}

.info-section ul {
  margin: 0;
  padding-left: 20px;
}

.info-section li {
  margin-bottom: 8px;
  color: #333;
}

.submission-section,
.history-section,
.review-section {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.review-section {
  border-top: 4px solid #f0c14b;
}

h3 {
  color: #333;
  margin-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
}

.loading,
.no-data {
  text-align: center;
  padding: 40px;
  color: #666;
}

.table-wrap {
  overflow-x: auto;
}

.essay-table {
  width: 100%;
  border-collapse: collapse;
}

.essay-table th {
  background-color: #42b983;
  color: white;
  padding: 12px;
  text-align: left;
  font-weight: bold;
  white-space: nowrap;
}

.essay-table td {
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: top;
}

.essay-table tr:hover td {
  background-color: #f8f9fa;
}

.overdue {
  color: #dc3545;
}

.overdue-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  background: #f8d7da;
  color: #721c24;
  border-radius: 10px;
  font-size: 12px;
}

.file-line {
  font-weight: 600;
  color: #333;
}

.date-line {
  font-size: 12px;
  color: #888;
  margin: 2px 0 6px;
}

.status-muted {
  color: #999;
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.status-badge.pending {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.approved {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.rejected {
  background-color: #f8d7da;
  color: #721c24;
}

.reject-note {
  margin-top: 6px;
  font-size: 13px;
  color: #c0392b;
}

.submit-btn,
.view-btn,
.approve-btn,
.reject-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

.submit-btn {
  background-color: #28a745;
  color: white;
}

.submit-btn:hover:not(:disabled) {
  background-color: #218838;
}

.submit-btn:disabled {
  background-color: #94d3a2;
  cursor: not-allowed;
}

.view-btn {
  background-color: #17a2b8;
  color: white;
  margin-right: 6px;
}

.view-btn:hover {
  background-color: #138496;
}

.approve-btn {
  background-color: #28a745;
  color: white;
  margin-right: 6px;
}

.approve-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reject-btn {
  background-color: #dc3545;
  color: white;
}

.reject-btn:hover {
  background-color: #c82333;
}

.review-actions {
  white-space: nowrap;
}

@media (max-width: 768px) {
  .submission-section,
  .history-section,
  .review-section {
    padding: 18px;
  }

  .essay-table th,
  .essay-table td {
    padding: 8px;
    font-size: 13px;
  }
}
</style>
